import * as React from 'react'
import { Link } from 'react-router-dom'
import { Archive, ArchiveRestore, PenTool, Plus, StickyNote, Trash2 } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { displayName, initials, relativeTime } from '@/lib/format'
import type { Board, Profile } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { useDirty, useDiscardGuard } from '@/hooks/use-unsaved'
import { DiscardPrompt } from '@/components/discard-prompt'
import {
  BLANK_TEMPLATE,
  BOARD_TEMPLATES,
  templateRows,
  type BoardTemplate,
} from '@/lib/board-templates'

export function BoardsPage() {
  const { currentTeam, isLead } = useAuth()
  const teamId = currentTeam?.id
  const [creating, setCreating] = React.useState(false)
  const [showArchived, setShowArchived] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) {
      return {
        boards: [] as Board[],
        counts: new Map<string, number>(),
        people: new Map<string, Profile>(),
      }
    }
    const { data: boards, error: berr } = await sb()
      .from('boards')
      .select('*')
      .eq('team_id', teamId)
      .order('updated_at', { ascending: false })
    if (berr) throw berr

    // One query for all object counts rather than N+1 per board.
    const { data: objs, error: oerr } = await sb()
      .from('board_objects')
      .select('board_id')
      .eq('team_id', teamId)
    if (oerr) throw oerr
    const counts = new Map<string, number>()
    for (const o of objs ?? []) counts.set(o.board_id, (counts.get(o.board_id) ?? 0) + 1)

    // Who made each board. Unfiltered on purpose — RLS limits profiles to
    // teammates — and read in one query rather than per card. A board whose
    // author has left the team still carries their id, and `on delete set
    // null` means a deleted account leaves a null rather than a dangling one.
    const { data: profiles, error: perr } = await sb().from('profiles').select('*')
    if (perr) throw perr
    const people = new Map<string, Profile>()
    for (const p of (profiles ?? []) as Profile[]) people.set(p.user_id, p)

    return { boards: (boards ?? []) as Board[], counts, people }
  }, [teamId])

  const shown = (data?.boards ?? []).filter((b) => b.archived === showArchived)
  const archivedCount = (data?.boards ?? []).filter((b) => b.archived).length

  async function setArchived(board: Board, archived: boolean) {
    const { error: err } = await sb().from('boards').update({ archived }).eq('id', board.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  async function remove(board: Board) {
    if (!window.confirm(`Delete “${board.title}” and everything on it?`)) return
    const { error: err } = await sb().from('boards').delete().eq('id', board.id)
    if (err) return window.alert(err.message)
    void refetch()
  }

  return (
    <Page
      title="Boards"
      description="Design activity boards — sticky notes, shapes, arrows, and people the whole team can move around together."
      actions={
        <>
          {archivedCount > 0 && (
            <Button variant="ghost" onClick={() => setShowArchived(!showArchived)}>
              {showArchived ? 'Active boards' : `Archived (${archivedCount})`}
            </Button>
          )}
          <Button onClick={() => setCreating(true)}>
            <Plus /> New board
          </Button>
        </>
      }
    >
      {error && <ErrorNote error={error} />}
      {loading && !data ? (
        <Loading />
      ) : shown.length === 0 ? (
        <EmptyState
          icon={PenTool}
          title={showArchived ? 'No archived boards' : 'No boards yet'}
          description={
            showArchived
              ? undefined
              : 'Create a board for a design exercise, an architecture sketch, or a retro.'
          }
          action={
            !showArchived && (
              <Button onClick={() => setCreating(true)}>
                <Plus /> Create the first board
              </Button>
            )
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((b) => (
            <Card key={b.id} className="group flex flex-col transition-colors hover:border-primary/40">
              <CardContent className="flex flex-1 flex-col p-4">
                <div className="flex items-start justify-between gap-2">
                  <Link to={`/boards/${b.id}`} className="min-w-0 font-medium hover:underline">
                    {b.title}
                  </Link>
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      onClick={() => void setArchived(b, !b.archived)}
                      aria-label={b.archived ? 'Restore board' : 'Archive board'}
                    >
                      {b.archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
                    </Button>
                    {isLead && (
                      <Button variant="ghost" size="icon-sm" onClick={() => void remove(b)} aria-label="Delete board">
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>

                {b.description && (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{b.description}</p>
                )}

                <div className="mt-auto flex items-center gap-2 pt-3">
                  <Badge variant="muted" className="shrink-0 gap-1 text-[10px]">
                    <StickyNote className="h-3 w-3" />
                    {data?.counts.get(b.id) ?? 0}
                  </Badge>
                  {/* The timestamp is what gives way when the row is tight: an
                      author truncated to "Chad M…" is still the answer to who
                      made this, while a truncated "edited 3 month…" is not an
                      answer to anything. */}
                  <span className="min-w-0 truncate text-xs text-muted-foreground">
                    edited {relativeTime(b.updated_at)}
                  </span>
                  <Author profile={b.created_by ? data?.people.get(b.created_by) : undefined} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {creating && teamId && (
        <BoardDialog
          teamId={teamId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false)
            void refetch()
          }}
        />
      )}
    </Page>
  )
}

/**
 * Who made a board, bottom-right of its card.
 *
 * Renders NOTHING when there is no author rather than "Unknown": boards made
 * before `boards.created_by` was ever written (0063) keep a null unless the
 * backfill could infer one from their first object, and a card that names
 * nobody reads better than one that names a person who does not exist.
 *
 * The initials carry the recognition at this size and the name carries the
 * certainty, so both ship; the name truncates and the avatar does not.
 */
function Author({ profile }: { profile: Profile | undefined }) {
  if (!profile) return null
  const name = displayName(profile)
  return (
    <span
      className="ml-auto flex shrink-0 items-center gap-1.5"
      title={`Created by ${name}`}
    >
      <Avatar className="h-5 w-5">
        <AvatarFallback className="text-[9px]">
          {initials(profile.display_name, profile.email)}
        </AvatarFallback>
      </Avatar>
      <span className="max-w-[7.5rem] truncate text-xs text-muted-foreground">{name}</span>
    </span>
  )
}

function BoardDialog({
  teamId,
  onClose,
  onSaved,
}: {
  teamId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [title, setTitle] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [background, setBackground] = React.useState<Board['background']>('dots')
  const [template, setTemplate] = React.useState<BoardTemplate>(BLANK_TEMPLATE)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const dirty = useDirty({ title, description, background, template: template.name })
  const guard = useDiscardGuard(dirty, onClose)

  // Each template has a background that suits it (the architecture diagram
  // wants a grid), but an explicit choice wins — so only follow the template
  // until someone touches the control themselves.
  const backgroundTouched = React.useRef(false)
  function pickTemplate(t: BoardTemplate) {
    setTemplate(t)
    if (!backgroundTouched.current) setBackground(t.background)
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return setError('Give the board a name.')
    setBusy(true)
    setError(null)
    try {
      const { data: board, error: err } = await sb()
        .from('boards')
        .insert({
          team_id: teamId,
          title: title.trim(),
          description: description.trim() || null,
          background,
        })
        .select('id')
        .single()
      if (err) throw err

      // One bulk insert for the whole layout. team_id is re-derived per row by
      // a trigger, so the value sent here is a formality.
      const rows = templateRows(template, board.id as string, teamId)
      if (rows.length > 0) {
        const { error: objErr } = await sb().from('board_objects').insert(rows)
        if (objErr) {
          // The board exists but is empty, which is a confusing half-state to
          // leave behind — take it back out and report the real failure.
          await sb().from('boards').delete().eq('id', board.id)
          throw objErr
        }
      }
      onSaved()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && guard.requestClose()}>
      <DialogContent className="sm:max-w-lg">
        <DiscardPrompt guard={guard} what="this board" />
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle>New board</DialogTitle>
            <DialogDescription>An infinite canvas your team edits together.</DialogDescription>
          </DialogHeader>
          <div className="my-4 space-y-4">
            <div className="space-y-2">
              <Label>Start from</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {BOARD_TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => pickTemplate(t)}
                    aria-pressed={template.id === t.id}
                    title={t.description}
                    className={cn(
                      'rounded-md border p-2 text-left transition-colors',
                      template.id === t.id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'hover:bg-accent',
                    )}
                  >
                    <TemplateThumb template={t} />
                    <div className="mt-1.5 truncate text-xs font-medium">{t.name}</div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{template.description}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="btitle">Name</Label>
              <Input
                id="btitle"
                autoFocus
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Service boundaries workshop"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bdesc">Description</Label>
              <Textarea id="bdesc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="bbg">Background</Label>
              <Select
                value={background}
                onValueChange={(v) => {
                  backgroundTouched.current = true
                  setBackground(v as Board['background'])
                }}
              >
                <SelectTrigger id="bbg">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dots">Dots</SelectItem>
                  <SelectItem value="grid">Grid</SelectItem>
                  <SelectItem value="blank">Blank</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={guard.requestClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              Create board
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * A miniature of the template's layout for the picker. Hand-described rectangles
 * rather than a render of the real objects: at 30px tall an honest render is an
 * unreadable smear, and this only has to convey the SHAPE — three lanes, a 2x2,
 * stacked layers.
 */
function TemplateThumb({ template }: { template: BoardTemplate }) {
  if (template.preview.length === 0) {
    return (
      <div className="flex h-[52px] w-full items-center justify-center rounded border border-dashed bg-muted/40 text-[10px] text-muted-foreground">
        empty
      </div>
    )
  }
  return (
    <div className="relative h-[52px] w-full overflow-hidden rounded bg-muted/40">
      {template.preview.map((r, i) => (
        <span
          key={i}
          className={cn('absolute rounded-[2px]', r.color ? `note-${r.color}` : '')}
          style={{
            left: `${r.x}%`,
            top: `${r.y}%`,
            width: `${r.w}%`,
            height: `${r.h}%`,
            background: r.outline ? 'transparent' : 'hsl(var(--note))',
            border: r.outline ? '1px dashed hsl(var(--muted-foreground) / 0.5)' : undefined,
            // The value proposition canvas is a square facing a circle, and that
            // silhouette is the whole reason it's recognizable at thumbnail size.
            borderRadius: r.round ? '50%' : undefined,
          }}
        />
      ))}
    </div>
  )
}
